import { TableFrame, Th, Td, NumTd } from './TableFrame';
import { pct, times } from './format';
import { certaintyTagClass, signalTagClass } from './labels';
import type { CertaintySignal, ReportLine } from './types';

interface BidCertaintyTableProps {
  lines: ReportLine[];
  /** Troubleshooter window, e.g. "16/10–18/10". */
  troubleshooterLabel: string | null;
}

interface SignalRule {
  name: string;
  measures: string;
  source: string;
  two: string;
  one: string;
  zero: string;
}

/** How each signal scores, as the skill computes it (delivery_report.py, certainty). */
const SIGNAL_RULES: SignalRule[] = [
  {
    name: 'Días a tope',
    measures: 'Días completos con gasto ≥ 98 % del tope diario (sin arranque ni pausas), ponderados por recencia',
    source: 'BigQuery + historial GCS',
    two: '≥ 90 %',
    one: '≥ 60 %',
    zero: '< 60 %',
  },
  {
    name: 'Margen CPM',
    measures: '1 − CPM reciente (ponderado por recencia) / techo de puja de la línea',
    source: 'BigQuery + API DV360',
    two: '≥ 50 %',
    one: '≥ 30 %',
    zero: '< 30 %',
  },
  {
    name: 'Holgura',
    measures: 'Impresiones frenadas por pacing + presupuesto ÷ ganadas',
    source: 'Troubleshooter GCS',
    two: '≥ 2×',
    one: '≥ 0,5×',
    zero: '< 0,5×',
  },
  {
    name: 'Bajo suelo',
    measures: '% de pujas por debajo de la puja mínima',
    source: 'Troubleshooter GCS',
    two: '< 15 %',
    one: '< 20 %',
    zero: '≥ 20 %',
  },
];

/** "If we lower the bid, do we keep buying?": level per line and its four signals. */
export function BidCertaintyTable({ lines, troubleshooterLabel }: BidCertaintyTableProps) {
  return (
    <div className="space-y-4">
      <TableFrame
        head={
          <>
            <Th>Línea</Th>
            <Th>Certeza</Th>
            <Th numeric>Días a tope</Th>
            <Th numeric>Margen CPM</Th>
            <Th numeric>Holgura</Th>
            <Th numeric>Bajo suelo</Th>
          </>
        }
      >
        {lines.map((line) => {
          const { cert } = line;
          return (
            <tr key={line.id} className="hover:bg-adv-sunken">
              <Td className="font-bold">{line.name}</Td>
              <Td>
                <span className={`adv-tag ${certaintyTagClass(cert.level)}`}>
                  {cert.level}
                  {cert.total !== null && (
                    <span className="adv-num">
                      · {cert.total}/{cert.max}
                    </span>
                  )}
                </span>
              </Td>
              <SignalCell signal={cert.signals.at_cap} format={(value) => pct(value, 0)} />
              <SignalCell signal={cert.signals.margin} format={(value) => pct(value, 0)} />
              <SignalCell signal={cert.signals.headroom} format={times} />
              <SignalCell signal={cert.signals.floor} format={(value) => pct(value, 1)} />
            </tr>
          );
        })}
      </TableFrame>

      <details className="text-adv-body-s">
        <summary className="cursor-pointer text-adv-secondary hover:text-adv-primary">
          Cómo se puntúa cada señal
        </summary>
        <div className="mt-3">
          <TableFrame
            head={
              <>
                <Th>Señal</Th>
                <Th>Qué mide</Th>
                <Th>Fuente</Th>
                <Th numeric>2 puntos</Th>
                <Th numeric>1 punto</Th>
                <Th numeric>0</Th>
              </>
            }
          >
            {SIGNAL_RULES.map((rule) => (
              <tr key={rule.name}>
                <Td className="font-bold whitespace-nowrap">{rule.name}</Td>
                <Td className="text-adv-secondary">{rule.measures}</Td>
                <Td className="text-adv-secondary whitespace-nowrap">
                  {rule.source}
                  {rule.source.startsWith('Troubleshooter') && troubleshooterLabel
                    ? ` ${troubleshooterLabel}`
                    : ''}
                </Td>
                <NumTd>{rule.two}</NumTd>
                <NumTd>{rule.one}</NumTd>
                <NumTd>{rule.zero}</NumTd>
              </tr>
            ))}
            <tr className="bg-adv-sunken font-bold">
              <Td>Certeza</Td>
              <Td colSpan={2} className="font-normal text-adv-secondary">
                Suma de las señales disponibles sobre su máximo
              </Td>
              <NumTd>Alta ≥ 7/8</NumTd>
              <NumTd>Media ≥ 4/8</NumTd>
              <NumTd>Baja &lt; 4/8</NumTd>
            </tr>
          </TableFrame>
        </div>
      </details>
    </div>
  );
}

function SignalCell({
  signal,
  format,
}: {
  signal: CertaintySignal;
  format: (value: number | null) => string;
}) {
  const [value, points] = signal;
  return (
    <NumTd>
      <span className="inline-flex items-center gap-2">
        {format(value)}
        {points !== null && <span className={`adv-tag ${signalTagClass(points)}`}>{points}</span>}
      </span>
    </NumTd>
  );
}
